<script lang="ts" setup>
defineEmits(['click']);

const props = defineProps<{
  to?: string;
  href?: string;
  target?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  prependIcon?: string;
  appendIcon?: string;
  color?: string;
  size?: string;
  disabled?: boolean;
  isLoading?: boolean;
  count?: number;
}>();

const computedClasses = computed(() => [
  `the-btn--${props.size ?? 'lg'}`,
  `the-btn--${props.color ?? 'primary'}`,
  {
    'is-loading': props.isLoading,
    'the-btn--disabled': props.disabled,
  },
]);
</script>

<template>
  <div class="the-btn__container">
    <AppLink
      v-if="to"
      :class="computedClasses"
      :target="target ?? '_self'"
      :to
      class="the-btn"
    >
      <component
        :is="`Svgo${prependIcon}`"
        v-if="prependIcon"
        class="the-btn__icon"
        filled
      />

      <slot />

      <component
        :is="`Svgo${appendIcon}`"
        v-if="appendIcon"
        class="the-btn__icon"
        filled
      />
    </AppLink>

    <a
      v-else-if="href"
      :class="computedClasses"
      :href
      :target="target ?? '_self'"
      class="the-btn"
    >
      <component
        :is="`Svgo${prependIcon}`"
        v-if="prependIcon"
        class="the-btn__icon"
        filled
      />

      <slot />

      <component
        :is="`Svgo${appendIcon}`"
        v-if="appendIcon"
        class="the_btn__icon"
        filled
      />
    </a>

    <button
      v-else
      :class="computedClasses"
      :disabled="disabled"
      :type="type ?? 'button'"
      class="the-btn"
      @click="$emit('click')"
    >
      <component
        :is="`Svgo${prependIcon}`"
        v-if="prependIcon"
        class="the-btn__icon"
        filled
      />

      <slot />

      <component
        :is="`Svgo${appendIcon}`"
        v-if="appendIcon"
        class="the-btn__icon"
        filled
      />
    </button>

    <div
      v-if="count"
      class="the-btn__count"
    >
      {{ count }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.the-btn {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: $bold;
  text-align: center;
  transition:
    opacity 0.3s,
    background-color 0.3s;
  -webkit-tap-highlight-color: transparent;

  &__container {
    position: relative;
  }

  &:hover {
    opacity: 0.7;
  }

  &--primary {
    background-color: $black;
    color: $white;

    &.is-loading {
      background: $black-80
        linear-gradient(
          -45deg,
          $black 25%,
          transparent 25%,
          transparent 50%,
          $black 50%,
          $black 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      cursor: wait !important;
      pointer-events: none;
      animation: load 2s linear infinite;
    }
  }

  &--secondary {
    background-color: $black-10;
    color: $black;

    &.is-loading {
      background: $black-20
        linear-gradient(
          -45deg,
          $black-40 25%,
          transparent 25%,
          transparent 50%,
          $black-40 50%,
          $black-40 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      cursor: wait;
      pointer-events: none;
      animation: load 2s linear infinite;
    }
  }

  &--outline {
    border: 1px solid $black;
    background-color: transparent;
    color: $black;

    &.is-loading {
      border-color: $black-60;
      background: $black-10
        linear-gradient(
          -45deg,
          $black-20 25%,
          transparent 25%,
          transparent 50%,
          $black-20 50%,
          $black-20 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      color: $black-80;
      cursor: wait !important;
      pointer-events: none;
      animation: load 2s linear infinite;
    }
  }

  &--accent {
    background-color: $accent;
    color: $white;

    &.is-loading {
      background: rgb(227 110 103)
        linear-gradient(
          -45deg,
          $accent 25%,
          transparent 25%,
          transparent 50%,
          $accent 50%,
          $accent 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      cursor: wait !important;
      pointer-events: none;
      animation: load 2s linear infinite;
    }
  }

  &--brick {
    background-color: $brick;
    color: $white;

    &.is-loading {
      background: rgb(217 86 76)
        linear-gradient(
          -45deg,
          $brick 25%,
          transparent 25%,
          transparent 50%,
          $brick 50%,
          $brick 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      cursor: wait !important;
      pointer-events: none;
      animation: load 2s linear infinite;
    }
  }

  &--violet {
    background-color: $violet;
    color: $white;

    &.is-loading {
      background: #7740bd
        linear-gradient(
          -45deg,
          $violet 25%,
          transparent 25%,
          transparent 50%,
          $violet 50%,
          $violet 75%,
          transparent 75%,
          transparent
        );
      background-size: 50px 50px;
      cursor: wait !important;
      pointer-events: none;
      animation: load 2s linear infinite;
    }

    &:hover {
      background: #7740bd;
      opacity: 1;
    }
  }

  &--lg {
    height: 48px;
  }

  &--md {
    height: 40px;
    font-size: $text-sm;
  }

  &--sm {
    height: 28px;
    border-radius: 8px;
    font-size: $text-xs;
  }

  &__icon {
    width: 16px;
    height: 16px;
  }

  &:disabled,
  &--disabled {
    background-color: $black-10;
    color: $black-60;
    cursor: not-allowed;
    pointer-events: none;
  }

  &__count {
    position: absolute;
    top: -5px;
    right: -5px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: $accent;
    color: $white;
    font-weight: $bold;
    font-size: $text-2xs;
  }
}

@keyframes load {
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: 50px 50px;
  }
}
</style>
